import React, { useEffect, useMemo, useState } from 'react';
import classes from './GenAiButton.module.scss';
import { Button, Link } from '@mui/material';
import { ReactComponent as GenAiIcon } from 'new/components/icons/genai.svg';
import { useDispatch, useSelector } from 'react-redux';
import { clearGenAiRecommendation, getDocRecommendation } from 'js/actions/recommendations';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { DOC_RECOMMENDATION_REQUEST } from 'js/constants/actionTypes';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import cx from 'classnames';
import useRequests from 'new/state/request/repository/useRequests';
import some from 'lodash/some';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { Integrations } from 'new/components/settings/Integrations';
import { loadGenAiConfigs } from 'js/actions/genAiConfigs';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import { KnowledgePanelProps } from 'new/elements/KnowledgePanel/KnowledgePanel';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { GreySpinner } from 'new/components/icons/GreySpinner/GreySpinner';
import { TooltipWithTitle } from 'new/elements/TooltipWithTitle';
import styles from './GenAiButton.module.scss';

interface GenAiButtonProps {
  description?: string;
  id: number;
  resourceType: string;
  onAccept: (suggestion: string) => void;
  /** When set, render a labeled button (e.g. empty-state CTA) instead of the icon. */
  label?: string;
}

export const GenAiButton = (props: GenAiButtonProps) => {
  const { id, resourceType, onAccept, description, label } = props;
  const [fabError, setError] = useState(false);
  const dispatch = useDispatch();
  const user = useCurrentUser();
  // Same global error surface as API failures (credentials, resource creation):
  // ErrorDisplay, with consistent styling, placement, and the "Show more"
  // expansion for long messages.
  const { showError } = useRequests();
  const loading = useIsLoading(DOC_RECOMMENDATION_REQUEST);

  const { recommendation, error } =
    useSelector((state: any) => state.recommendations?.[resourceType]?.[id]) || {};

  const { genAiConfigs } = useSelector((state: any) => state.entities);

  const activeConfig = useMemo(() => {
    return some(genAiConfigs, (config) => config.status === 'active');
  }, [genAiConfigs]);

  useEffect(() => {
    if (id && resourceType) {
      dispatch(clearGenAiRecommendation({ id, resourceType }));
    }
    dispatch(loadGenAiConfigs());
  }, []);

  useDidUpdateEffect(() => {
    if (!loading) {
      if (error) {
        setTimeout(() => {
          setError(false);
        }, 4000);
      }

      if (recommendation && !error) {
        if (typeof onAccept === 'function') {
          onAccept(recommendation);
        }
      } else if (!!error) {
        // Surface the failure through the standard global ErrorDisplay instead
        // of dumping it inline next to the button. Honor the server's message
        // (it can carry useful detail); ErrorDisplay handles long/ugly bodies
        // via its "Show more" expansion.
        setError(true);
        showError(
          typeof error === 'string' && error.trim()
            ? error
            : 'Could not generate documentation. Please try again.',
        );
      }
    }
  }, [loading, error, recommendation]);

  const onClickFab = () => {
    dispatch(getDocRecommendation({ id, resourceType }));
  };

  const ButtonWrapper = activeConfig ? React.Fragment : KnowledgePanel;
  const buttonWrapperProps = activeConfig
    ? undefined
    : ({
        trigger: 'hover',
        enterDelay: 400,
        hideLine: true,
        knowledgeContent: <InfoPanel user={user} active={activeConfig} />,
      } as KnowledgePanelProps);

  return (
    <div className={classes.buttonContainer}>
      <TooltipWithTitle
        theme="dark"
        placement="bottom"
        arrow={true}
        title={<div>Create Documentation with GenAI</div>}
        description={
          <div className={styles.infoPanel}>
            <InfoPanel user={user} active={activeConfig} />
          </div>
        }
      >
        {label ? (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={!activeConfig}
            onClick={onClickFab}
            startIcon={loading ? <GreySpinner /> : <GenAiIcon className={classes.icon} />}
            sx={{ textTransform: 'none', borderRadius: '16px' }}
          >
            {label}
          </Button>
        ) : (
          <IconButton
            sx={{
              border: '1px solid var(--color-greyscale-200)',
              borderRadius: '16px',
              padding: 'var(--spacing-sm)',
              width: '32px',
              color: 'var(--color-greyscale-600)',
              '&:hover': {
                backgroundColor: 'var(--color-greyscale-50)',
              },
            }}
            className={cx(classes.fab, { [classes.fab_error]: fabError }, styles.iconButton)}
            disabled={!activeConfig}
            onClick={onClickFab}
          >
            {loading ? <GreySpinner /> : <GenAiIcon className={classes.icon} />}
          </IconButton>
        )}
      </TooltipWithTitle>
    </div>
  );
};

function InfoPanel({ user, active }) {
  function getDescription() {
    if (active) {
      return <>Instantly generate documentation for your resources with GenAI.</>;
    }
    if (!user.isAdmin) {
      return (
        <>GenAI documentation is disabled for your organization. Contact your admin to enable it.</>
      );
    }
    return (
      <>
        {`GenAI documentation is disabled for your organization. `}
        <Link
          to={Integrations.screenConfig.path + '?activeTab=gen_ai'}
          target="_blank"
          className={styles.genAiLink}
        >
          Enable it here.
        </Link>
      </>
    );
  }
  return <div className={styles.infoPanel}>{getDescription()}</div>;
}
